import React, { useState, useEffect } from "react";
import { useExpertInfoStore } from "@src/store/ExpertInfoStore";

import { styled, keyframes } from "styled-components";
import { Common } from "@src/styles/Common";

import MgInput from "@src/components/input/MgInput";
import ExpertSelectInfo from "@src/components/SignupExpert/ExpertSelectInfo";
import UserInfoProfileUploadContainer from "@src/components/SignupExpert/Containers/UserInfoProfileUploader";
import MgDialog from "@src/components/Dialog";
import { CounselingCategory } from "@src/api/expert/expertInfo";
import Header from "@src/components/Header";
import UserOfficeInfoBox from "@src/components/SignupExpert/UserOfficeInfoCard";

import WaitConfirm from "@src/assets/Signup/waitConfirm.png";
import { transferToNative, WebAppBridge, getDeviceType } from "@src/lib/WebAppBridge";
import { getExpertInfo, getExpertApprovalStatus } from "@src/api/expert/expertInfo";
import { updateExpertInfo } from "@src/api/expert/expertInfo";
import ApproveCheck from "./ApproveCheck";
import ImageModal from "./ImageModal";

const ExpertInfos = () => {
    const { form, isEditing, setPartialState } = useExpertInfoStore();
    const [loading, setLoading] = useState(true);
    const [isApproved, setIsApproved] = useState(false);
    const [isRejected, setIsRejected] = useState(false);
    const [showPendingPopup, setShowPendingPopup] = useState(false);
    const [joinError, setJoinError] = useState<string>("");
    const [showImageModal, setShowImageModal] = useState(false);

    // 원본 전문가 정보 저장
    const [originalInfo, setOriginalInfo] = useState<any>(null);
    const [originalInfoInitialized, setOriginalInfoInitialized] = useState(false);
    const [wasAuctionAgent, setWasAuctionAgent] = useState(false);

    // currentInfo 기본 구조
    const currentInfo = form.infos[0] ?? {
        phone: "",
        officeInfo: undefined,
        counselingCategories: [],
        career: "{}",
        careerObj: {},
        qualification: "",
        introduction: "",
        auctionAgentCertFileUrl: "",
        updatedAt: new Date().toISOString(),
        type: "",
        file: { url: "" },
        bankName: "",
        bankAccount: "",
    };

    // 전문가 정보 조회
    useEffect(() => {
        const fetchExpertInfo = async () => {
            try {
                const [data, approvalStatus] = await Promise.all([getExpertInfo(), getExpertApprovalStatus()]);

                setIsApproved(approvalStatus.isApproved);
                setIsRejected(approvalStatus.isRejected);

                const infoWithCareerObj = {
                    ...data.info,
                    counselingCategories: data.info.counselingCategories || [], // 명시적으로 설정
                    careerObj: data.info.career
                        ? typeof data.info.career === "string"
                            ? JSON.parse(data.info.career)
                            : data.info.career
                        : {},
                    career: JSON.stringify(data.info.career || {}),
                    file: data.info.file || { url: "" }, // 백업용 file.url 유지
                    qualification: data.info.qualification || "",
                    introduction: data.info.introduction || "",
                };

                // isEditing 중이면 store를 덮어쓰지 않음
                if (!isEditing) {
                    setPartialState({
                        infos: [infoWithCareerObj],
                        no: data.expertNo,
                        profile: data.profile, // 최상위 profile 필드
                        name: "전문가",
                    });

                    // API 응답 직후에 wasAuctionAgent 설정 (타이밍 이슈 해결)
                    if (!originalInfoInitialized) {
                        const categories = data.info.counselingCategories || [];
                        const hasAuctionAgent = Array.isArray(categories) && categories.includes("매수신청대리인");
                        setWasAuctionAgent(hasAuctionAgent);
                        setOriginalInfo(JSON.parse(JSON.stringify(infoWithCareerObj)));
                        setOriginalInfoInitialized(true);
                    }
                }

                setLoading(false);
            } catch (err) {
                console.error("전문가 정보 조회 실패", err);
                setLoading(false);
            }
        };

        fetchExpertInfo();
    }, [isEditing]);

    const handleChangeField = (key: keyof typeof currentInfo, e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const value = e.target.value;
        setPartialState({
            infos: [{ ...currentInfo, [key]: value }],
        });
    };

    // 검증 로직
    const validateAndSubmitForm = (runType: "validate" | "submit"): boolean => {
        const careerObj = currentInfo.careerObj || {};

        if (!currentInfo.counselingCategories || currentInfo.counselingCategories.length === 0) {
            if (runType === "submit") {
                setJoinError("전문가 유형을 하나 이상 선택해주세요.");
            }
            return false;
        }

        // 경력 값이 1000 이하면 false 반환
        const invalidCareer = currentInfo.counselingCategories.some((category) => {
            const value = (currentInfo.careerObj as Record<string, string | number | undefined>)[category];
            if (
                value === undefined ||
                value === null ||
                (typeof value === "string" && (value.trim() === "" || isNaN(Number(value)))) ||
                (typeof value === "number" && isNaN(value))
            ) {
                return true;
            }
            const numValue = Number(value);
            return numValue <= 1000;
        });

        if (invalidCareer) {
            if (runType === "submit") {
                setJoinError("경력을 올바르게 입력해주세요.");
            }
            return false;
        }

        // 은행명, 계좌번호
        if (!currentInfo.bankName || !currentInfo.bankAccount) {
            if (runType === "submit") {
                setJoinError("은행명과 계좌번호를 모두 입력해주세요.");
            }
            return false;
        }

        // 중개/매수신청대리인 => 중개사무소 검증
        if (
            (currentInfo.counselingCategories.includes("중개") || currentInfo.counselingCategories.includes("매수신청대리인")) &&
            (!currentInfo.officeInfo || !currentInfo.officeInfo.name || !currentInfo.officeInfo.ceo)
        ) {
            if (runType === "submit") {
                setJoinError("중개사무소 정보를 입력해주세요.");
            }
            return false;
        }

        // 매수신청대리인 => 등록증 검증
        if (currentInfo.counselingCategories.includes("매수신청대리인") && !currentInfo.auctionAgentCertFileUrl) {
            if (runType === "submit") {
                setJoinError("매수신청대리인 등록증을 업로드해주세요.");
            }
            return false;
        }

        return true;
    };

    useEffect(() => {
    if (getDeviceType() !== "android") return;

    WebAppBridge.onGetCertificateImage = (imageUrl: string) => {
        setPartialState({
            infos: [
                {
                    ...form.infos[0],
                    auctionAgentCertFileUrl: imageUrl,
                },
            ],
        });
    };

    return () => {
        WebAppBridge.onGetCertificateImage = undefined as any;
    };
}, [form.infos, setPartialState]);


    // 수정 토글
    const handleEditToggle = async () => {
        if (!isEditing) {
            // "수정하기" => 스크롤 상승
            const container = document.querySelector(".expert-container");
            if (container) {
                container.scrollTo({
                    top: 0,
                    behavior: "smooth",
                });
            }
            useExpertInfoStore.setState({ isEditing: true });
        } else {
            const isValid = validateAndSubmitForm("submit");
            if (isValid === false) return;

            try {
                const current = JSON.parse(JSON.stringify(form.infos[0]));
                if (JSON.stringify(current) === JSON.stringify(originalInfo)) {
                    useExpertInfoStore.setState({ isEditing: false });
                    return;
                }

                const accessToken = localStorage.getItem("accessToken");
                if (!accessToken) throw new Error("토큰 없음");

                const body = {
                    expertNo: form.no,
                    phone: currentInfo.phone,
                    profile: form.profile,
                    officeInfo: currentInfo.officeInfo
                        ? {
                              name: currentInfo.officeInfo.name || "",
                              ceo: currentInfo.officeInfo.ceo || "",
                              phone: currentInfo.officeInfo.phone || "",
                              address: currentInfo.officeInfo.address || "",
                              registrationNumber: currentInfo.officeInfo.registrationNumber || "",
                              type: currentInfo.officeInfo.type || "",
                              pnu: currentInfo.officeInfo.pnu || "",
                          }
                        : {
                              name: "",
                              ceo: "",
                              phone: "",
                              address: "",
                              registrationNumber: "",
                              type: "",
                              pnu: "",
                          },
                    counselingCategories: currentInfo.counselingCategories,
                    career: currentInfo.careerObj || {},
                    qualification: currentInfo.qualification || "",
                    introduction: currentInfo.introduction || "",
                    isAuctionAgent: currentInfo.counselingCategories.includes("매수신청대리인"),
                    auctionAgentCertFileUrl: currentInfo.auctionAgentCertFileUrl || "",
                    bankName: currentInfo.bankName || "",
                    bankAccount: currentInfo.bankAccount || "",
                };

                await updateExpertInfo(body);

                useExpertInfoStore.setState({ isEditing: false });

                const currentCategories = currentInfo.counselingCategories || [];
                const currentHasAuctionAgent = Array.isArray(currentCategories) && currentCategories.includes("매수신청대리인");
                const shouldShowPopup = currentHasAuctionAgent && !isApproved && !isRejected && !wasAuctionAgent;

                if (shouldShowPopup) {
                    setShowPendingPopup(true);
                }

                // 수정 완료 시 wasAuctionAgent 상태 갱신
                // 현재 경매대행이 선택되어 있으면 wasAuctionAgent를 true로 설정
                if (currentHasAuctionAgent) {
                    setWasAuctionAgent(true);
                }

                // 수정 후 원본값 갱신
                setOriginalInfo(JSON.parse(JSON.stringify(form.infos[0])));
            } catch (err) {
                console.error("전문가 정보 수정 실패", err);
                setJoinError("전문가 정보 수정 중 오류가 발생했습니다.");
            }
        }
    };

    const handlePendingPopupConfirm = () => {
        setShowPendingPopup(false); // 팝업 닫기
    };

    const handleCloseWebView = () => {
        transferToNative("done", "closeWebView");
    };

    if (loading) return <div></div>; // 로딩중 화면
    return (
        <Background>
            <Header
                title={isEditing ? "전문가 회원 정보 수정" : "전문가 회원 정보"}
                hasCloseBtn={false}
                onClose={handleCloseWebView}
            />
            <Expert_Container className="expert-container">
                {/* 프로필 업로드 */}
                <InputBox className="profile-box">
                    <UserInfoProfileUploadContainer />
                </InputBox>

                {/* 전문가 선택 */}
                <InputBox>
                    <InputHeader>
                        <div className="title">
                            <div className="star">*</div>
                            {isEditing ? "원하는 전문가 (복수선택 가능)" : "전문가"}
                        </div>
                    </InputHeader>
                    {!isEditing && currentInfo.counselingCategories.includes("매수신청대리인") && (
                        <ApproveCheck isApproved={isApproved} isRejected={isRejected} />
                    )}
                    <ExpertSelectInfo
                        isApproved={isApproved}
                        availableCategories={getFilteredCategories(isEditing, isApproved)}
                    />
                </InputBox>

                {(currentInfo.counselingCategories.includes("중개") ||
                    currentInfo.counselingCategories.includes("매수신청대리인")) &&
                !isEditing ? (
                    <InputBox>
                        <InputHeader>
                            <div className="title">{isEditing ? "중개사무소" : "중개사무소 정보"}</div>
                        </InputHeader>
                        {currentInfo.officeInfo && (
                            <UserOfficeInfoBox
                                officeInfo={{
                                    name: currentInfo.officeInfo.name || "",
                                    ceo: currentInfo.officeInfo.ceo || "",
                                    registrationNumber: currentInfo.officeInfo.registrationNumber || "",
                                    phone: currentInfo.officeInfo.phone || "",
                                    address: currentInfo.officeInfo.address || "",
                                }}
                            />
                        )}
                    </InputBox>
                ) : null}

                {/* 매수신청대리인 등록증 미리보기 */}
                {currentInfo.counselingCategories.includes("매수신청대리인") &&
                    currentInfo.auctionAgentCertFileUrl &&
                    !isEditing && (
                        <InputBox>
                            <InputHeader>
                                <div className="title">{isEditing ? "매수신청대리인 등록증" : "매수신청대리인 등록증 정보"}</div>
                            </InputHeader>
                            <CertPreviewWrap>
                                <CertPreviewImg
                                    src={currentInfo.auctionAgentCertFileUrl}
                                    alt="매수신청대리인 등록증 미리보기"
                                    onClick={() => setShowImageModal(true)}
                                />
                            </CertPreviewWrap>
                        </InputBox>
                    )}

                {/* 이미지 모달 */}
                {showImageModal && (
                    <ImageModal imageUrl={currentInfo.auctionAgentCertFileUrl || ""} onClose={() => setShowImageModal(false)} />
                )}

                {/* 경력 */}
                {currentInfo.counselingCategories.length > 0 && (
                    <InputBox>
                        <InputHeader $isEditing={isEditing}>
                            <div className="star">*</div>
                            <div className="title">{isEditing ? "경력 (시작 연도를 입력해주세요)" : "경력(시작 연도)"}</div>
                        </InputHeader>
                        {COUNCELING_CATEGORIES_ORDER.filter((category) =>
                            currentInfo.counselingCategories.includes(category as CounselingCategory),
                        ).map((category) => {
                            const yearValue = currentInfo.careerObj?.[category as CounselingCategory] || "";
                            const isYearValid = yearValue !== "" && !isNaN(Number(yearValue));
                            return (
                                <YearRow key={category}>
                                    <YearLabel>{category === "매수신청대리인" ? "경매대행" : category}</YearLabel>
                                    <YearInputWrap>
                                        <MgInput
                                            width="100%"
                                            value={yearValue}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                                const value = e.target.value;
                                                setPartialState((prev) => ({
                                                    infos: prev.infos.map((info, idx) =>
                                                        idx === 0
                                                            ? {
                                                                  ...info,
                                                                  careerObj: {
                                                                      ...info.careerObj,
                                                                      [category]: value,
                                                                  },
                                                              }
                                                            : info,
                                                    ),
                                                }));
                                            }}
                                            maxLength={4}
                                            placeholder="YYYY"
                                            noBorder
                                            bgColor="#FBF8F8"
                                            inputMode="numeric"
                                            readOnly={!isEditing}
                                        />
                                        <YearSuffix>년</YearSuffix>
                                    </YearInputWrap>
                                </YearRow>
                            );
                        })}
                    </InputBox>
                )}

                {/* 은행명 */}
                <InputBox>
                    <InputHeader $isEditing={isEditing}>
                        <div className="star">*</div>
                        <div className="title">{isEditing ? "계좌번호 정보" : "계좌번호"}</div>
                    </InputHeader>
                    <MgInput
                        value={currentInfo.bankName || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeField("bankName", e)}
                        maxLength={20}
                        placeholder="은행명을 입력해주세요."
                        readOnly={!isEditing}
                        noBorder
                        bgColor="#FBF8F8"
                    />
                    <MgInput
                        value={currentInfo.bankAccount || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeField("bankAccount", e)}
                        maxLength={20}
                        placeholder="계좌번호를 입력해주세요."
                        readOnly={!isEditing}
                        noBorder
                        bgColor="#FBF8F8"
                    />
                </InputBox>

                {/* 자격 */}
                <InputBox style={{ position: "relative" }}>
                    <InputHeader>
                        <div className="title">{isEditing ? "자격 (선택)" : "자격"}</div>
                    </InputHeader>
                    <MgInput
                        as="textarea"
                        value={currentInfo.qualification || ""}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleChangeField("qualification", e)}
                        maxLength={200}
                        placeholder="자격 사항을 입력해주세요."
                        readOnly={!isEditing}
                        noBorder
                        bgColor="#FBF8F8"
                        height="200px"
                    />
                    <LimitText $editing={isEditing}>{`${(currentInfo.qualification || "").length} / 200`}</LimitText>
                </InputBox>

                {/* 소개 */}
                <InputBox style={{ position: "relative" }}>
                    <InputHeader>
                        <div className="title">{isEditing ? "소개 (선택)" : "소개"}</div>
                    </InputHeader>
                    <MgInput
                        as="textarea"
                        value={currentInfo.introduction || ""}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleChangeField("introduction", e)}
                        maxLength={500}
                        placeholder="소개 글을 입력해주세요."
                        readOnly={!isEditing}
                        noBorder
                        bgColor="#FBF8F8"
                        height="200px"
                    />
                    <LimitText $editing={isEditing}>{`${(currentInfo.introduction || "").length} / 500`}</LimitText>
                </InputBox>

                <BottomSpace />

                {/* 수정 버튼 */}
                <BtnStyle onClick={handleEditToggle} isEditing={isEditing}>
                    {isEditing ? "수정 완료" : "수정하기"}
                </BtnStyle>

                {/* 에러 팝업 */}
                {joinError && (
                    <MgDialog open={true} onCloseView={() => setJoinError("")} contents={<p>{joinError}</p>} confirmText="확인" />
                )}
                <MgDialog
                    open={showPendingPopup}
                    onCloseView={handlePendingPopupConfirm}
                    contents={
                        <div style={{ textAlign: "center", padding: "24px 0", width: "240px" }}>
                            <p style={{ fontSize: 22, fontWeight: 800, marginBottom: 12 }}>관리자 승인 대기중</p>
                            <img
                                src={WaitConfirm}
                                alt="승인 대기중"
                                style={{ width: 140, height: 140, objectFit: "contain", margin: "8px 0 12px" }}
                            />
                            <p style={{ fontSize: 14, color: "#6b7685", marginTop: 12 }}>
                                매수신청대리인의 경우
                                <br />
                                관리자의 별도 승인이 필요합니다.
                                <br />
                                승인 후 경매대행을 할 수 있습니다.
                            </p>
                        </div>
                    }
                    confirmText="확인"
                />
            </Expert_Container>
        </Background>
    );
};

export default ExpertInfos;

const BottomSpace = styled.div`
    width: 100%;
    height: 80px;
    margin-bottom: 120px;
`;

const Background = styled.div`
    position: relative;
    width: 100%;
    min-height: 100vh;
    background-position: center;
    overflow: hidden;
`;

const Expert_Container = styled.div`
    display: flex;
    height: 100vh;
    flex-direction: column;
    padding: 24px;
    overflow-y: scroll;
    background: #ffffff;
    min-height: calc(100vh - 48px); /* 패딩 제외한 최소 높이 */

    .subTitle {
        font-size: 12px;
        font-weight: 400;
        color: #b8b8b8;
        line-height: 140%;
        margin-bottom: 8px;
    }

    @media screen and (min-width: 768px) {
        position: absolute;
        width: 336px;
        min-height: 700px;
        top: 60px;
        left: 50%;
        transform: translateX(-50%);
        margin-bottom: 60px;
        border-radius: 12px;
        background: #ffffff;
        overflow-y: auto;
    }
`;

const InputHeader = styled.div<{ $isEditing?: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
    margin-bottom: 8px;

    .star {
        color: ${Common.colors.notiColor};
        display: ${(props) => (props.$isEditing ? "block" : "none")};
    }

    .title {
        color: ${Common.colors.normalTextColor};
        font-size: 16px;
        font-weight: 400;
        line-height: 140%;
    }
`;

const InputBox = styled.div`
    margin-top: 16px;
    margin-bottom: 24px;

    &.profile-box {
        display: flex;
        justify-content: center;
    }

    & > input + input {
        margin-top: 8px;
    }
`;

const YearRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    padding: 10px 0;
`;

const YearLabel = styled.div`
    font-size: 16px;
    font-weight: 700;
    color: #0c002c;
`;

const YearInputWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100px;
`;

const YearSuffix = styled.span`
    font-size: 16px;
    color: #140c2c;
`;

const BtnStyle = styled.button<{ isEditing?: boolean }>`
    position: fixed;
    bottom: 16px;
    left: 50%;
    transform: translateX(-50%);
    padding: 12px 14px;
    width: calc(100% - 32px);
    border-radius: 12px;
    background: #2275ee;
    color: #ffffff;
    font-weight: 400;
    font-size: 18px;
    cursor: ${(props) => (props.isEditing ? "pointer" : "default")};
    transition: background-color 0.2s;
    z-index: 10;

    &.active {
        background: ${Common.colors.aucMarkerColor};
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.8;
    }

    @media screen and (max-width: 768px) {
        width: calc(100% - 32px);
    }
`;

const CertPreviewWrap = styled.div`
    display: flex;
    justify-content: left;
    align-items: center;
    width: 100%;
    padding: 12px 0;
`;

const CertPreviewImg = styled.img`
    width: 120px;
    height: 120px;
    object-fit: cover;
    border-radius: 12px;
    border: 1px solid #e0e0e0;
    cursor: pointer;
    transition: all 0.2s ease-out;

    &:hover {
        transform: scale(1.05);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
`;

const LimitText = styled.div<{ $editing?: boolean }>`
    position: absolute;
    right: 12px;
    bottom: 8px;
    font-size: 12px;
    color: #b8b8b8;
    background: transparent;
    text-align: right;
    pointer-events: none;
    display: ${({ $editing }) => ($editing ? "block" : "none")};
`;

// 편집 모드나 승인 상태에 따라 보여줄 카테고리 필터링
const getFilteredCategories = (isEditing: boolean, isApproved: boolean): CounselingCategory[] => {
    return COUNCELING_CATEGORIES_ORDER;
};

export const COUNCELING_CATEGORIES_ORDER: CounselingCategory[] = ["중개", "매물컨설팅", "매수신청대리인", "임장동행"];
